// DEFCON TERMINAL - Audio Manager
// Main audio management class for the game

import { SoundGenerator } from './SoundGenerator';
import { ModemSounds } from './ModemSounds';
import { AUDIO_FILES } from '@shared/constants';

// Re-export ModemSounds for external access to static methods
export { ModemSounds };

/**
 * Sound IDs for easy reference throughout the application
 */
export type SoundId =
  | 'modemConnect'
  | 'modemHandshake'
  | 'dialTone'
  | 'phoneDialing'
  | 'carrierTone'
  | 'connectionEstablished'
  | 'terminalBeep'
  | 'keypress'
  | 'error'
  | 'success'
  | 'alertKlaxon'
  | 'warDialSequence';

/**
 * Audio preference storage key
 */
const AUDIO_PREFS_KEY = 'defcon_terminal_audio_prefs';

/**
 * Audio preferences interface
 */
interface AudioPreferences {
  muted: boolean;
  volume: number;
}

/**
 * AudioManager - Central audio control for DEFCON TERMINAL
 *
 * Features:
 * - Web Audio API for low-latency playback
 * - Procedural sound generation (no external files required)
 * - Optional external audio file support
 * - Persistent mute/volume preferences
 * - Browser autoplay restriction handling
 */
export class AudioManager {
  private context: AudioContext | null = null;
  private soundGenerator: SoundGenerator | null = null;
  private modemSounds: ModemSounds | null = null;
  private masterGain: GainNode | null = null;

  private initialized: boolean = false;
  private userInteracted: boolean = false;
  private muted: boolean = false;
  private volume: number = 0.7;

  // Cache for loaded external audio files
  private audioCache: Map<string, AudioBuffer> = new Map();
  private loadingPromises: Map<string, Promise<AudioBuffer | null>> = new Map();

  // Currently playing sounds for stop functionality
  private activeSounds: Map<string, AudioBufferSourceNode[]> = new Map();

  constructor() {
    this.loadPreferences();
  }

  /**
   * Initialize the audio context (must be called after user interaction)
   */
  async initialize(): Promise<boolean> {
    if (this.initialized && this.context) {
      return true;
    }

    try {
      // Create audio context
      this.context = new (window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)();

      // Create master gain node
      this.masterGain = this.context.createGain();
      this.masterGain.connect(this.context.destination);
      this.masterGain.gain.setValueAtTime(
        this.muted ? 0 : this.volume,
        this.context.currentTime
      );

      // Initialize sound generators
      this.soundGenerator = new SoundGenerator(this.context);
      this.modemSounds = new ModemSounds(this.context, this.soundGenerator);

      // Update volume on generators
      this.soundGenerator.setMasterVolume(this.muted ? 0 : this.volume);
      this.modemSounds.setVolume(this.muted ? 0 : this.volume);

      this.initialized = true;
      this.userInteracted = true;

      // Resume context if suspended (browser autoplay policy)
      if (this.context.state === 'suspended') {
        await this.context.resume();
      }

      console.log('DEFCON TERMINAL Audio System initialized');
      return true;
    } catch (error) {
      console.error('Failed to initialize audio system:', error);
      return false;
    }
  }

  /**
   * Check if audio system is ready for playback
   */
  isReady(): boolean {
    return this.initialized && this.context !== null && this.context.state === 'running';
  }

  /**
   * Check if user has interacted (enabling audio)
   */
  hasUserInteracted(): boolean {
    return this.userInteracted;
  }

  /**
   * Resume audio context (call on user interaction)
   */
  async resume(): Promise<void> {
    if (this.context && this.context.state === 'suspended') {
      await this.context.resume();
    }
  }

  /**
   * Play a sound by ID
   */
  async play(soundId: SoundId, options?: { phoneNumber?: string }): Promise<void> {
    if (!this.initialized) {
      const success = await this.initialize();
      if (!success) return;
    }

    if (this.muted || !this.context || !this.soundGenerator || !this.modemSounds) {
      return;
    }

    // Ensure context is running
    if (this.context.state === 'suspended') {
      await this.context.resume();
    }

    let source: AudioBufferSourceNode | null = null;

    try {
      // Try to load external audio file first, fall back to procedural
      const externalBuffer = await this.tryLoadExternalAudio(soundId);

      if (externalBuffer) {
        source = this.playBuffer(externalBuffer);
      } else {
        // Use procedural audio
        source = this.playProceduralSound(soundId, options);
      }

      if (source) {
        this.trackActiveSound(soundId, source);
      }
    } catch (error) {
      console.warn(`Failed to play sound ${soundId}:`, error);
    }
  }

  /**
   * Play procedural sound based on sound ID
   */
  private playProceduralSound(
    soundId: SoundId,
    options?: { phoneNumber?: string }
  ): AudioBufferSourceNode | null {
    if (!this.soundGenerator || !this.modemSounds) return null;

    switch (soundId) {
      case 'modemConnect':
        return this.modemSounds.playModemConnect(options?.phoneNumber);

      case 'modemHandshake':
        return this.modemSounds.playHandshake();

      case 'dialTone':
        return this.modemSounds.playDialTone();

      case 'phoneDialing':
        return options?.phoneNumber
          ? this.modemSounds.playPhoneDialing(options.phoneNumber)
          : null;

      case 'carrierTone':
        return this.modemSounds.playCarrierTone();

      case 'connectionEstablished':
        return this.modemSounds.playConnectionEstablished();

      case 'terminalBeep': {
        const beep = this.soundGenerator.generateTerminalBeep();
        this.soundGenerator.playSound(beep);
        return beep;
      }

      case 'keypress': {
        const click = this.soundGenerator.generateKeypress();
        this.soundGenerator.playSound(click);
        return click;
      }

      case 'error': {
        const error = this.soundGenerator.generateErrorBuzz();
        this.soundGenerator.playSound(error);
        return error;
      }

      case 'success': {
        const success = this.soundGenerator.generateSuccessSound();
        this.soundGenerator.playSound(success);
        return success;
      }

      case 'alertKlaxon': {
        const klaxon = this.soundGenerator.generateAlertKlaxon();
        this.soundGenerator.playSound(klaxon);
        return klaxon;
      }

      default:
        console.warn(`Unknown sound ID: ${soundId}`);
        return null;
    }
  }

  /**
   * Try to load external audio file for a sound ID
   */
  private async tryLoadExternalAudio(soundId: SoundId): Promise<AudioBuffer | null> {
    const filePath = this.getAudioFilePath(soundId);
    if (!filePath) return null;

    // Check cache first
    if (this.audioCache.has(filePath)) {
      return this.audioCache.get(filePath) || null;
    }

    // Check if already loading
    if (this.loadingPromises.has(filePath)) {
      return this.loadingPromises.get(filePath) || null;
    }

    // Start loading
    const loadPromise = this.loadAudioFile(filePath);
    this.loadingPromises.set(filePath, loadPromise);

    const buffer = await loadPromise;
    this.loadingPromises.delete(filePath);

    if (buffer) {
      this.audioCache.set(filePath, buffer);
    }

    return buffer;
  }

  /**
   * Get audio file path for a sound ID
   */
  private getAudioFilePath(soundId: SoundId): string | null {
    const pathMap: Partial<Record<SoundId, string>> = {
      modemConnect: AUDIO_FILES.modemConnect,
      modemHandshake: AUDIO_FILES.modemHandshake,
      terminalBeep: AUDIO_FILES.terminalBeep,
      alertKlaxon: AUDIO_FILES.alertKlaxon,
      keypress: AUDIO_FILES.keypress,
      error: AUDIO_FILES.error,
      success: AUDIO_FILES.success,
    };

    return pathMap[soundId] || null;
  }

  /**
   * Load an audio file and decode it
   */
  private async loadAudioFile(path: string): Promise<AudioBuffer | null> {
    if (!this.context) return null;

    try {
      const response = await fetch(path);
      if (!response.ok) {
        // File doesn't exist, fall back to procedural
        return null;
      }

      const arrayBuffer = await response.arrayBuffer();
      const audioBuffer = await this.context.decodeAudioData(arrayBuffer);
      return audioBuffer;
    } catch {
      // File not found or decode error, fall back to procedural
      return null;
    }
  }

  /**
   * Play an AudioBuffer
   */
  private playBuffer(buffer: AudioBuffer): AudioBufferSourceNode | null {
    if (!this.context || !this.masterGain) return null;

    const source = this.context.createBufferSource();
    source.buffer = buffer;
    source.connect(this.masterGain);
    source.start(this.context.currentTime);

    return source;
  }

  /**
   * Track active sound for stop functionality
   */
  private trackActiveSound(soundId: string, source: AudioBufferSourceNode): void {
    if (!this.activeSounds.has(soundId)) {
      this.activeSounds.set(soundId, []);
    }

    const sources = this.activeSounds.get(soundId)!;
    sources.push(source);

    // Clean up when sound ends
    source.onended = () => {
      const index = sources.indexOf(source);
      if (index > -1) {
        sources.splice(index, 1);
      }
    };
  }

  /**
   * Stop a specific sound
   */
  stop(soundId: SoundId): void {
    const sources = this.activeSounds.get(soundId);
    if (sources) {
      sources.forEach((source) => {
        try {
          source.stop();
        } catch {
          // Already stopped
        }
      });
      this.activeSounds.set(soundId, []);
    }
  }

  /**
   * Stop all currently playing sounds
   */
  stopAll(): void {
    this.activeSounds.forEach((sources) => {
      sources.forEach((source) => {
        try {
          source.stop();
        } catch {
          // Already stopped
        }
      });
    });
    this.activeSounds.clear();
  }

  /**
   * Set master volume (0-1)
   */
  setVolume(volume: number): void {
    this.volume = Math.max(0, Math.min(1, volume));

    if (this.masterGain && this.context && !this.muted) {
      this.masterGain.gain.setValueAtTime(this.volume, this.context.currentTime);
    }

    if (this.soundGenerator && !this.muted) {
      this.soundGenerator.setMasterVolume(this.volume);
    }

    if (this.modemSounds && !this.muted) {
      this.modemSounds.setVolume(this.volume);
    }

    this.savePreferences();
  }

  /**
   * Get current volume
   */
  getVolume(): number {
    return this.volume;
  }

  /**
   * Mute all audio
   */
  mute(): void {
    this.muted = true;

    if (this.masterGain && this.context) {
      this.masterGain.gain.setValueAtTime(0, this.context.currentTime);
    }

    if (this.soundGenerator) {
      this.soundGenerator.setMasterVolume(0);
    }

    if (this.modemSounds) {
      this.modemSounds.setVolume(0);
    }

    this.savePreferences();
  }

  /**
   * Unmute audio (restore previous volume)
   */
  unmute(): void {
    this.muted = false;

    if (this.masterGain && this.context) {
      this.masterGain.gain.setValueAtTime(this.volume, this.context.currentTime);
    }

    if (this.soundGenerator) {
      this.soundGenerator.setMasterVolume(this.volume);
    }

    if (this.modemSounds) {
      this.modemSounds.setVolume(this.volume);
    }

    this.savePreferences();
  }

  /**
   * Toggle mute state
   */
  toggleMute(): boolean {
    if (this.muted) {
      this.unmute();
    } else {
      this.mute();
    }
    return this.muted;
  }

  /**
   * Check if audio is muted
   */
  isMuted(): boolean {
    return this.muted;
  }

  /**
   * Check if audio is enabled (not muted)
   */
  isEnabled(): boolean {
    return !this.muted;
  }

  /**
   * Load preferences from localStorage
   */
  private loadPreferences(): void {
    try {
      const stored = localStorage.getItem(AUDIO_PREFS_KEY);
      if (stored) {
        const prefs: AudioPreferences = JSON.parse(stored);
        this.muted = prefs.muted ?? false;
        this.volume = prefs.volume ?? 0.7;
      }
    } catch {
      // Use defaults if localStorage fails
      this.muted = false;
      this.volume = 0.7;
    }
  }

  /**
   * Save preferences to localStorage
   */
  private savePreferences(): void {
    try {
      const prefs: AudioPreferences = {
        muted: this.muted,
        volume: this.volume,
      };
      localStorage.setItem(AUDIO_PREFS_KEY, JSON.stringify(prefs));
    } catch {
      // Silently fail if localStorage is unavailable
    }
  }

  /**
   * Preload audio files for faster playback
   */
  async preloadSounds(): Promise<void> {
    const soundIds: SoundId[] = [
      'modemConnect',
      'modemHandshake',
      'terminalBeep',
      'keypress',
      'error',
      'success',
      'alertKlaxon',
    ];

    // Try to preload external files (failures are ok, we have procedural fallback)
    await Promise.all(soundIds.map((id) => this.tryLoadExternalAudio(id)));
  }

  /**
   * Clean up resources
   */
  dispose(): void {
    this.stopAll();

    if (this.context) {
      this.context.close();
    }

    this.context = null;
    this.soundGenerator = null;
    this.modemSounds = null;
    this.masterGain = null;
    this.audioCache.clear();
    this.loadingPromises.clear();
    this.activeSounds.clear();
    this.initialized = false;
  }

  /**
   * Get the raw audio context (for advanced usage)
   */
  getAudioContext(): AudioContext | null {
    return this.context;
  }

  /**
   * Get the sound generator (for advanced usage)
   */
  getSoundGenerator(): SoundGenerator | null {
    return this.soundGenerator;
  }

  /**
   * Get the modem sounds generator (for advanced usage)
   */
  getModemSounds(): ModemSounds | null {
    return this.modemSounds;
  }

  /**
   * Play the full war dial sequence with 7-digit phone number
   * Returns timeline data for synchronized text display
   */
  async playWarDialSequence(): Promise<{
    phoneData: { number: string; formatted: string };
    routingDigits: string;
    timeline: Array<{ phase: string; startTime: number; duration: number; description: string }>;
    totalDuration: number;
  } | null> {
    if (!this.initialized) {
      const success = await this.initialize();
      if (!success) return null;
    }

    if (this.muted || !this.modemSounds) {
      // Return mock data for muted state
      const phoneData = ModemSounds.generate7DigitNumber();
      const routingDigits = ModemSounds.generate24RoutingDigits();
      return {
        phoneData,
        routingDigits,
        timeline: [
          { phase: 'dialing', startTime: 0, duration: 1.05, description: `DIALING: ${phoneData.formatted}` },
          { phase: 'pause', startTime: 1.05, duration: 0.6, description: 'WAITING FOR CARRIER...' },
          { phase: '2600hz', startTime: 1.65, duration: 1.0, description: '2600Hz TONE - TRUNK SEIZED' },
          { phase: 'routing', startTime: 2.65, duration: 5.76, description: `MF ROUTING: ${routingDigits}` },
          { phase: 'connecting', startTime: 8.41, duration: 0.6, description: 'ROUTING COMPLETE - CONNECTING...' },
          { phase: 'ringing', startTime: 9.01, duration: 5.5, description: 'RINGING...' },
          { phase: 'modem', startTime: 14.51, duration: 1.8, description: 'MODEM ANSWERING - ESTABLISHING CONNECTION' },
        ],
        totalDuration: 16.31,
      };
    }

    if (this.context && this.context.state === 'suspended') {
      await this.context.resume();
    }

    const result = this.modemSounds.playWarDialSequence();

    // Calculate total duration from timeline
    const lastPhase = result.timeline[result.timeline.length - 1];
    const totalDuration = lastPhase.startTime + lastPhase.duration;

    return {
      phoneData: result.phoneData,
      routingDigits: result.routingDigits,
      timeline: result.timeline,
      totalDuration,
    };
  }
}

// Singleton instance for easy access throughout the application
let audioManagerInstance: AudioManager | null = null;

/**
 * Get the global AudioManager instance
 */
export function getAudioManager(): AudioManager {
  if (!audioManagerInstance) {
    audioManagerInstance = new AudioManager();
  }
  return audioManagerInstance;
}

/**
 * Play a sound using the global audio manager
 * Convenience function for quick sound playback
 */
export async function playSound(
  soundId: SoundId,
  options?: { phoneNumber?: string }
): Promise<void> {
  const manager = getAudioManager();
  await manager.play(soundId, options);
}

/**
 * Initialize audio on user interaction
 * Call this on the first click/keypress
 */
export async function initializeAudioOnInteraction(): Promise<boolean> {
  const manager = getAudioManager();
  return manager.initialize();
}
